import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../card.jsx";
import { Input } from "../input.jsx";
import { Textarea } from "../textarea.jsx";
import { Toggle } from "../toggle.tsx";
import Badge from "../badge.jsx";
import { Button } from "../button.jsx";
import { ChevronDown, Plus, X, ShieldCheck, PhoneCall, Globe2 } from "lucide-react";
import BackModule from "./back.tsx";
import CountryCodeDropDownAndPhoneField from "../countrycodedropdownandphonefield.tsx";
import axios from "axios";
import Swal from "sweetalert2";
import LanguageTabs from "./languagetabs.tsx";
import TagMultiSelect from "./tagmultiselect.tsx";

const AgentChip = ({ label, onRemove }: { label: string; onRemove?: () => void }) => (
    <Badge
        variant="outline"
        className="flex items-center gap-1 rounded-full border-slate-300 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700"
    >
        {label}
        {onRemove && (
            <button
                type="button"
                className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-slate-200"
                onClick={onRemove}
            >
                <X className="h-3 w-3" />
            </button>
        )}
    </Badge>
);

const PriceRow = ({
    label,
    helper,
    currency,
    placeholder,
    value,
    onChange,
}: {
    label: string;
    helper?: string;
    currency?: string;
    placeholder?: string;
    value?: string;
    onChange: (ele: any) => void,
}) => (
    <div className="grid gap-1">
        <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-slate-700">{label}</label>
            {helper && <span className="text-[11px] text-slate-400">{helper}</span>}
        </div>
        <div className="flex items-center gap-2">
            <div className="relative flex-1">
                {currency && (
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-400">
                        {currency}
                    </span>
                )}
                <Input
                    className={`h-9 rounded-xl border-slate-200 bg-slate-50 text-sm focus-visible:ring-tele-primary/40 ${currency ? "pl-10" : "pl-3"
                        }`}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                />
            </div>
        </div>
    </div>
);

interface AgentDetailProps {
    tab?: string;
    setTab: (tab: string) => void;
    baseUrl: string,
    currentUser: any,
    client: any,
    setSelectedAgent: (agent: any) => void,
    selectedAgent: any
}

const AgentDetailModule: React.FC<AgentDetailProps> = ({ tab, setTab, baseUrl, currentUser, client, setSelectedAgent, selectedAgent }) => {
    const showBackBtn = localStorage.getItem("previousPage") ? true : false;
    const [phoneNumber, setPhoneNumber] = useState(selectedAgent?.company_no ?? '');
    const [countryCode, setCountryCode] = useState({ code: selectedAgent?.country_code ?? "+47", country: "Norge", flag: "🇳🇴" });
    const [phoneBill, setPhoneBill] = useState(selectedAgent?.phonebill ?? '');
    const [phoneBillCode, setPhoneBillCode] = useState({ code: selectedAgent?.phonebill_code ?? "+47", country: "Norge", flag: "🇳🇴" });
    const [isTeamLeader, setIsTeamLeader] = useState(false || selectedAgent?.is_group_leader === "0" ? false : true);
    const [teamLeaders, setTeamLeaders] = useState([{}]);
    const [teamMembers, setTeamMembers] = useState([{}]);
    const [groupLeaderUid, setGroupLeaderUid] = useState(selectedAgent?.is_group_leader ?? "0");
    // const [teamMemberUids, setTeamMemberUids] = ([{}]);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [agentName, setAgentName] = useState(selectedAgent?.name ?? "");
    const [email, setEmail] = useState(selectedAgent?.email ?? "");
    const default_configs = client ? JSON.parse(client.default_configs) : {};

    const [IVR, setIVR] = useState((selectedAgent?.permissions?.WebRTC === "1" ? false : true) || true);
    const [WebRTC, setWebRTC] = useState((selectedAgent?.permissions?.WebRTC === "1" ? true : false) || false);
    const [video, setVideo] = useState((selectedAgent?.can_video_call === "1" ? true : false) || false);
    const [canStream, setCanStream] = useState((selectedAgent?.permissions?.can_stream === "1" ? true : false) || false);
    const [canReport, setCanReport] = useState((selectedAgent?.permissions?.can_report === "1" ? true : false) || false);
    const [booking, setBooking] = useState((selectedAgent?.permissions?.booking === "1" ? true : false) || false);

    const [customerMinutePrice, setCustomerMinutePrice] = useState(selectedAgent?.customer_minute_price ?? "30");
    const [minutePriceAgent, setMinutePriceAgent] = useState(selectedAgent?.minute_price_agent ?? "55.50");
    const [minutePriceAgentShow, setMinutePriceAgentShow] = useState("55.50");
    const [minutePriceGroupLeader, setMinutePriceGroupLeader] = useState(selectedAgent?.minute_price_group_leader ?? "14.50");
    const [minutePriceGroupLeaderShow, setMinutePriceGroupLeaderShow] = useState("14.50");
    const [minutePriceClient, setMinutePriceClient] = useState(selectedAgent?.minute_price_client ?? "30.00");
    const [minutePriceClientShow, setMinutePriceClientShow] = useState("30.00");
    const [callStartupFee, setCallStartupFee] = useState(selectedAgent?.customer_call_startup_fee ?? "5");

    const [debounceTimer, setDebounceTimer] = useState<null>(null);

    const [isPasswordMatched, setIsPassowrdMatched] = useState(true);

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!password || !confirmPassword) return;

        if (debounceTimer) clearTimeout(debounceTimer);

        const timer = setTimeout(() => {

            if (password !== confirmPassword) {
                setIsPassowrdMatched(false);
            } else {
                setIsPassowrdMatched(true);
            }

        }, 1500);

        setDebounceTimer(timer);

        return () => clearTimeout(timer);
    }, [password, confirmPassword]);

    useEffect(() => {
        let onePercent = parseFloat(customerMinutePrice) / 100;
        let chargePrice = onePercent * client.rate_for_invoicing;
        let agentPrice = parseFloat(minutePriceAgent) * (chargePrice / 100);
        let leaderPrice = parseFloat(minutePriceGroupLeader) * (chargePrice / 100);
        let clientPrice = parseFloat(minutePriceClient) * (chargePrice / 100);

        setMinutePriceAgentShow(agentPrice.toFixed(2).toString());
        setMinutePriceGroupLeaderShow(leaderPrice.toFixed(2).toString());
        setMinutePriceClientShow(clientPrice.toFixed(2).toString());
    }, [minutePriceAgent, minutePriceGroupLeader, minutePriceClient, customerMinutePrice, client])


    const [minutePriceConference, setMinutePriceConference] = useState(selectedAgent?.minute_price_conference ?? "300");
    const [conferencePriceAgent, setConferencePriceAgent] = useState(selectedAgent?.conference_price_agent ?? "70");
    const [conferencePriceAgentShow, setConferencePriceAgentShow] = useState("");
    const [conferencePriceGroupLeader, setConferencePriceGroupLeader] = useState(selectedAgent?.conference_price_group_leader ?? "10");
    const [conferencePriceGroupLeaderShow, setConferencePriceGroupLeaderShow] = useState("10");
    const [conferencePriceClient, setConferencePriceClient] = useState(selectedAgent?.conference_price_client ?? "20");
    const [conferencePriceClientShow, setConferencePriceClientShow] = useState("20");
    useEffect(() => {
        let onePercent = parseFloat(minutePriceConference) / 100;
        let chargePrice = onePercent * client.conference_rate_for_invoicing;
        let agentPrice = parseFloat(conferencePriceAgent) * (chargePrice / 100);
        let leaderPrice = parseFloat(conferencePriceGroupLeader) * (chargePrice / 100);
        let clientPrice = parseFloat(conferencePriceClient) * (chargePrice / 100);

        setConferencePriceAgentShow(agentPrice.toFixed(2).toString());
        setConferencePriceGroupLeaderShow(leaderPrice.toFixed(2).toString());
        setConferencePriceClientShow(clientPrice.toFixed(2).toString());
    }, [conferencePriceAgent, conferencePriceGroupLeader, conferencePriceClient, minutePriceConference, client])

    const handleSaveAgentClick = async () => {
        setLoading(true);
        var data = new FormData();
        data.append("token", currentUser?.token);
        data.append("name", agentName);
        data.append("email", email);
        data.append("company_no", phoneNumber);
        data.append("phone_code", countryCode.code);
        data.append("phonebill_code", phoneBillCode.code);
        data.append("phonebill", phoneBill);
        data.append("is_group_leader", isTeamLeader.toString());
        data.append("group_leader_uid", groupLeaderUid);
        data.append("can_video_call", video.toString());
        data.append("customer_minute_price", customerMinutePrice);
        data.append("customer_call_startup_fee", callStartupFee);
        data.append("minute_price_agent", minutePriceAgent);
        data.append("minute_price_group_leader", minutePriceGroupLeader);
        data.append("minute_price_client", minutePriceClient);
        data.append("minute_price_conference", minutePriceConference);

        data.append("WebRTC", WebRTC.toString());
        data.append("video", video.toString());
        data.append("canStream", canStream.toString());
        data.append("canReport", canReport.toString());
        data.append("booking", booking.toString());
        data.append("client_uid", client?.uid);

        if(selectedAgent?.uid){
            data.append("agent_uid", selectedAgent?.uid);
        }

        const formatted: any = {};

        for (const code in langData) {
            if (code && code !== "undefined") {
                formatted[code.toLowerCase()] = langData[code].description || "";
            }
        }
        data.append("description", JSON.stringify(formatted));
        data.append("team_members", JSON.stringify(selectedMembers));
        data.append("skill_Id", selectedSkills.join(","));

        try {
            const response = await axios.post(baseUrl + '/createAgent', data);

            if (response.data.status === "OK") {
                Swal.fire({
                    title: 'Success',
                    icon: 'success',
                    text: response.data.data,
                })
            } else {
                Swal.fire({
                    title: 'Error',
                    icon: 'error',
                    text: response.data.data,
                })
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    const getTeamLeadersAndMembers = async () => {
        try {
            const response = await axios.get(baseUrl + '/getTeamLeadersAndMembers', { params: { token: currentUser.token, client_uid: client?.uid } });

            if (response.data.status === "OK") {
                setTeamLeaders(response.data.data.leaders);
                setTeamMembers(response.data.data.members);
            }
        } catch (err) {
            console.error(err);
        }
    }

    let languages: any = [];
    if (client) {
        languages = [
            client?.languages.includes("english") && { code: "en", label: "English", flag: "🇺🇸" },
            client?.languages.includes("norwegian") && { code: "no", label: "Norwegian", flag: "🇳🇴" },
            client?.languages.includes("swedish") && { code: "se", label: "Swedish", flag: "🇸🇪" },
            client?.languages.includes("danish") && { code: "dk", label: "Danish", flag: "🇩🇰" },
            client?.languages.includes("german") && { code: "gr", label: "German", flag: "GR" },
            client?.languages.includes("french") && { code: "fr", label: "French", flag: "FR" },
            client?.languages.includes("spanish") && { code: "sp", label: "Spanish", flag: "SP" },
        ];
    }

    useEffect(() => {
        if (!client) return;
        getTeamLeadersAndMembers();
    }, [client, baseUrl])

    const agentDescriptions = selectedAgent ? JSON.parse(selectedAgent.description) : null;

    const [langData, setLangData] = useState(
        Object.fromEntries(
            languages.map((lang: any) => [
                lang.label,
                { enabled: false, description: selectedAgent ? agentDescriptions?.[lang?.label?.toLowerCase()] : "" }
            ])
        )
    );

    const getSkills = async () => {
        try {
            const response = await axios.get(baseUrl + '/getSkills', { params: { client_uid: client?.uid } });

            if (response.data.status === "OK") {
                setSkills(response.data.data);
            }
        } catch (err) {
            console.error(err);
        }
    }

    useEffect(() => {
        getSkills();
    }, [client])
    const [skills, setSkills] = useState([{ id: '', name: '' }]);
    const [selectedMembers, setSelectedMembers] = useState(selectedAgent?.is_group_leader === "1" ? selectedAgent?.team_members : []);
    const [selectedSkills, setSelectedSkills] = useState((selectedAgent?.skill_Id !== null ? selectedAgent?.skill_Id.split(",") : []) ?? []);

    const handleManageSkillsClick = (to: string) => {
        localStorage.setItem("previousPage", "Agent detail");
        setTab(to);
    }
    const handleIVRChange = () => {
        setIVR(prev => {
            const newIVR = !prev;      // toggle IVR
            if (newIVR) setWebRTC(false); // only turn off WEBrtc if IVR is on
            return newIVR;
        });
    };

    const handleWebRTCChange = () => {
        setWebRTC(prev => {
            const newWebRTC = !prev;   // toggle WEBrtc
            if (newWebRTC) setIVR(false); // only turn off IVR if WEBrtc is on
            return newWebRTC;
        });
    };
    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex flex-col items-center">
                    <div className="w-12 h-12 border-4 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                    <p className="mt-3 text-gray-600 animate-pulse">Laster ...</p>
                </div>
            </div>
        );
    }
    return (
        <div className="min-h-screen bg-slate-50 px-6 py-8 text-slate-900">
            {showBackBtn && (
                <BackModule setTab={setTab} label={localStorage.getItem("previousPage") || ""} />
            )}
            <div className="mx-auto flex max-w-6xl flex-col gap-6">
                {/* Header */}
                <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
                    <div>
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-tele-primary">
                            <ShieldCheck className="h-4 w-4" />
                            Teletorget · Agent Management
                        </div>
                        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
                            {selectedAgent ? 'Update agent' : 'New agent'}
                        </h1>
                        <p className="mt-1 max-w-2xl text-sm text-slate-500">
                            Configure profile, skills and payout rules for a Teletorget agent. All
                            changes are applied in real time across chat, phone and video.
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center justify-end gap-3">
                        <Button
                            children="Cancel"
                            variant="secondary"
                            className="rounded-full border border-slate-200 bg-white px-4 text-xs font-medium"
                            onClick={() => setTab("Agenter")}
                        />
                        <Button className="rounded-full bg-tele-primary px-5 text-xs font-semibold text-white hover:bg-tele-primary/90"
                            onClick={handleSaveAgentClick}>
                            Save agent
                        </Button>
                    </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
                    {/* Left column – profile & content */}
                    <div className="flex flex-col gap-6">
                        {/* Agent profile */}
                        <Card className="border-none bg-white shadow-sm shadow-slate-100">
                            <CardHeader className="border-b border-slate-100 pb-4">
                                <CardTitle className="flex items-center justify-between text-sm font-semibold text-slate-800">
                                    <span>Agent profile</span>
                                    <span className="text-[11px] font-normal text-slate-400">
                                        Used across all Teletorget touchpoints
                                    </span>
                                </CardTitle>
                            </CardHeader>

                            {/* Core profile */}
                            <CardContent className="grid gap-4 pt-4">
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div className="grid gap-1.5">
                                        <label className="text-xs font-semibold text-slate-700">Agent name</label>
                                        <Input
                                            placeholder="John Doe"
                                            value={agentName}
                                            className="h-9 rounded-xl border-slate-200 bg-slate-50 text-sm focus-visible:ring-tele-primary/40"
                                            onChange={(e) => setAgentName(e.target.value)}
                                        />
                                    </div>

                                    <div className="grid gap-1.5 mt-2">
                                        <label className="text-xs font-semibold text-slate-700">Email address</label>
                                        <Input
                                            placeholder="agent@teletorget.no"
                                            type="email"
                                            value={email}
                                            className="h-9 rounded-xl border-slate-200 bg-slate-50 text-sm focus-visible:ring-tele-primary/40"
                                            onChange={(e) => setEmail(e.target.value)}
                                            readOnly={selectedAgent?.email ? true : false}
                                        />
                                    </div>

                                </div>

                                <div className="grid gap-1 5 mt-2">
                                    <div className="grid gap-1.5 md:grid-cols-[1.2fr_1fr]">
                                        <div className="grid gap-1.5">
                                            <label className="text-xs font-semibold text-slate-700">Country no</label>
                                            <CountryCodeDropDownAndPhoneField phoneNumber={phoneNumber} setPhoneNumber={setPhoneNumber} countryCode={countryCode} setCountryCode={setCountryCode} />
                                        </div>
                                        <div className="grid gap-1 5">
                                            <label className="text-xs font-semibold text-slate-700">Username</label>
                                            <Input
                                                disabled={true}
                                                placeholder="agent_xxxxx"
                                                type="text"
                                                className="h-9 rounded-xl border-slate-200 bg-slate-50 text-sm focus-visible:ring-tele-primary/40"
                                                defaultValue={selectedAgent?.uid}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="grid gap-1 5 mt-2">
                                    <div className="grid gap-1.5 md:grid-cols-[1.2fr_1fr]">
                                        <div className="grid gap-1.5">
                                            <label className="text-xs font-semibold text-slate-700">Strex no</label>
                                            <CountryCodeDropDownAndPhoneField phoneNumber={phoneBill} setPhoneNumber={setPhoneBill} countryCode={phoneBillCode} setCountryCode={setPhoneBillCode} />
                                        </div>
                                    </div>
                                </div>


                                <div className="grid gap-4 md:grid-cols-[1.2fr_1fr]">
                                    <div className="grid gap-1.5">
                                        <label className="text-xs font-semibold text-slate-700">Agent code</label>
                                        <Input
                                            disabled={true}
                                            placeholder="4-digit code"
                                            className="h-9 max-w-[200px] rounded-xl border-slate-200 bg-slate-50 text-sm focus-visible:ring-tele-primary/40"
                                            value={selectedAgent?.code}
                                            readOnly
                                        />
                                    </div>

                                    <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5">
                                        <div>
                                            <p className="text-xs font-semibold text-slate-700">Team leader</p>
                                            <p className="text-[11px] text-slate-400">Can manage team members and pricing</p>
                                        </div>
                                        <Toggle label={""} checked={isTeamLeader} onChange={() => setIsTeamLeader(v => !v)} bordering={true} insideLabel={"OnOffSwitch"} />
                                    </div>
                                </div>

                                <div className="grid gap-4 md:grid-cols-2">
                                    <div className="grid gap-1.5">
                                        <label className="text-xs font-semibold text-slate-700">Team leader</label>
                                        <select onChange={(e) => setGroupLeaderUid(e.target.value)} disabled={isTeamLeader} className="h-9 rounded-xl border border-slate-200 bg-slate-50 text-sm focus-visible:ring-tele-primary/40">
                                            <option selected={selectedAgent?.group_leader_uid === '0' ? true : false} disabled value="">Select team leader</option>
                                            {teamLeaders.map((leader) => (
                                                <option selected={selectedAgent?.group_leader_uid === leader?.uid ? true : false} value={leader?.uid}>{leader?.name}</option>
                                            ))}
                                        </select>
                                        <p className="text-[11px] text-slate-400">Choose who this agent reports to</p>
                                    </div>

                                    <div className="grid gap-1.5">
                                        <label className="text-xs font-semibold text-slate-700">Team Members</label>
                                        <TagMultiSelect
                                            options={teamMembers}
                                            selected={selectedMembers}
                                            setSelected={setSelectedMembers}
                                            helper={"Select team members..."}
                                        />
                                        <p className="text-[11px] text-slate-400">Choose who this agent reports to</p>
                                    </div>

                                </div>
                            </CardContent>
                            {/* Login & security on top */}
                            <CardContent className="grid gap-3 pt-4">
                                <div className="grid gap-1.5">
                                    <label className="text-xs font-semibold text-slate-700">Password</label>
                                    <Input
                                        type="password"
                                        placeholder="Set a secure password"
                                        className={`h-9 rounded-xl border-slate-200 bg-slate-50 text-sm focus-visible:ring-tele-primary/40 ${isPasswordMatched ? '' : 'border-red-500'}`}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                </div>
                                <div className="grid gap-1.5">
                                    <label className="text-xs font-semibold text-slate-700">Confirm password</label>
                                    <Input
                                        type="password"
                                        placeholder="Set a secure password"
                                        className={`h-9 rounded-xl border-slate-200 bg-slate-50 text-sm focus-visible:ring-tele-primary/40 ${isPasswordMatched ? '' : 'border-red-500'}`}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                    />
                                </div>
                                <p className="text-[11px] text-slate-400">
                                    If empty, Teletorget auto-generates secure onboarding credentials.
                                </p>
                            </CardContent>
                        </Card>

                        {/* Skills & languages */}
                        <Card className="border-none bg-white shadow-sm shadow-slate-100">
                            <CardHeader className="border-b border-slate-100 pb-4">
                                <CardTitle className="flex items-center justify-between text-sm font-semibold text-slate-800">
                                    <span>Skills & languages</span>
                                    <span className="text-[11px] font-normal text-slate-400">
                                        Controls routing and what customers see
                                    </span>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="grid gap-5 pt-4">
                                {/* Skills */}
                                <div className="grid gap-2">
                                    <div className="flex items-center justify-between">
                                        <label className="text-xs font-semibold text-slate-700">Assign skills</label>
                                        <button onClick={() => handleManageSkillsClick("Ferdigheter")} className="flex items-center gap-1 text-[11px] font-medium text-tele-primary hover:underline">
                                            <Plus className="h-3 w-3" />
                                            Manage skill list
                                        </button>
                                    </div>
                                    <div className="grid">
                                        <TagMultiSelect
                                            options={skills}
                                            selected={selectedSkills}
                                            setSelected={setSelectedSkills}
                                            helper={"Select skills..."}
                                        />
                                    </div>
                                </div>

                                {/* Languages */}
                                {client && languages && (
                                    <LanguageTabs languages={languages} langData={langData} setLangData={setLangData} />
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right column – pricing & access */}
                    <div className="flex flex-col gap-6">
                        {/* Realtime channels */}
                        <Card className="border-none bg-white shadow-sm shadow-slate-100">
                            <CardHeader className="border-b border-slate-100 pb-4">
                                <CardTitle className="flex items-center justify-between text-sm font-semibold text-slate-800">
                                    <span className="flex items-center gap-2">
                                        <PhoneCall className="h-4 w-4 text-tele-primary" />
                                        Channel availability
                                    </span>
                                    <span className="text-[11px] font-normal text-slate-400">
                                        Control where this agent is live
                                    </span>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="grid gap-3 pt-4">
                                {[
                                    { label: "IVR", helper: "1:1 Audio calls", checked: IVR, onchange: handleIVRChange },
                                    default_configs?.WebRTC && { label: "WebRTC", helper: "1:1 Audio And Video Via WebRTC", checked: WebRTC, onchange: handleWebRTCChange },
                                    default_configs?.video === "yes" && { label: "Video", helper: "1:1 and conference calls", checked: video, onchange: () => setVideo(v => !v) },
                                    { label: "Streaming", helper: 'Stream events', checked: canStream, onchange: () => setCanStream(v => !v) },
                                    { label: "Report", helper: 'Will receive reports i.e monthly', checked: canReport, onchange: () => setCanReport(v => !v) },
                                    { label: "Booking", helper: 'Allow booking for consultation', checked: booking, onchange: () => setBooking(v => !v) },
                                ].map((c) => (
                                    <div
                                        key={c?.label}
                                        className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5"
                                    >
                                        <div>
                                            <p className="text-xs font-semibold text-slate-700">{c?.label}</p>
                                            <p className="text-[11px] text-slate-400">{c?.helper}</p>
                                        </div>
                                        <Toggle label={""} checked={c?.checked} onChange={c?.onchange} bordering={true} insideLabel={"OnOffSwitch"} />
                                    </div>
                                ))}
                            </CardContent>
                        </Card>

                        {/* Pricing */}
                        <Card className="border-none bg-white shadow-sm shadow-slate-100">
                            <CardHeader className="border-b border-slate-100 pb-4">
                                <CardTitle className="flex items-center justify-between text-sm font-semibold text-slate-800">
                                    <span>Pricing & split</span>
                                    <span className="flex items-center gap-1 text-[11px] font-normal text-slate-400">
                                        <Globe2 className="h-3.5 w-3.5" /> {client?.currency} · Per minute & conference
                                    </span>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="grid gap-6 pt-4">
                                <div className="grid gap-3">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                                        Per-minute pricing
                                    </p>
                                    <PriceRow
                                        label="Customer price per minute"
                                        helper="What the customer sees (incl. VAT)"
                                        currency={client?.currency}
                                        placeholder="40"
                                        value={customerMinutePrice}
                                        onChange={(e) => setCustomerMinutePrice(e.target.value)}
                                    />
                                    <PriceRow
                                        label="Startup fee"
                                        helper="Optional call setup fee"
                                        currency={client?.currency}
                                        placeholder="0–250"
                                        value={callStartupFee}
                                        onChange={(e) => setCallStartupFee(e.target.value)}
                                    />

                                    <div className="grid gap-3 rounded-xl bg-slate-50 px-3 py-3">
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                                            Split
                                        </p>
                                        <div className="grid gap-2 md:grid-cols-3">
                                            <div className="grid gap-1">
                                                <label className="text-[11px] font-medium text-slate-700">Agent %</label>
                                                <Input
                                                    placeholder="71.43"
                                                    className="h-9 rounded-xl border-slate-200 bg-white text-sm focus-visible:ring-tele-primary/40"
                                                    value={minutePriceAgent}
                                                    onChange={(e) => setMinutePriceAgent(e.target.value)}
                                                />
                                                <Input
                                                    placeholder=""
                                                    className="h-9 rounded-xl border-slate-200 bg-white text-sm focus-visible:ring-tele-primary/40"
                                                    value={minutePriceAgentShow}
                                                    onChange={(e) => setMinutePriceAgentShow(e.target.value)}
                                                />
                                            </div>
                                            <div className="grid gap-1">
                                                <label className="text-[11px] font-medium text-slate-700">Team leader %</label>
                                                <Input
                                                    placeholder="23.81"
                                                    className="h-9 rounded-xl border-slate-200 bg-white text-sm focus-visible:ring-tele-primary/40"
                                                    value={minutePriceGroupLeader}
                                                    onChange={(e) => setMinutePriceGroupLeader(e.target.value)}
                                                />
                                                <Input
                                                    placeholder="23.81"
                                                    className="h-9 rounded-xl border-slate-200 bg-white text-sm focus-visible:ring-tele-primary/40"
                                                    value={minutePriceGroupLeaderShow}
                                                    onChange={(e) => setMinutePriceGroupLeaderShow(e.target.value)}
                                                />
                                            </div>
                                            <div className="grid gap-1">
                                                <label className="text-[11px] font-medium text-slate-700">Client %</label>
                                                <Input
                                                    placeholder="4.76"
                                                    className="h-9 rounded-xl border-slate-200 bg-white text-sm focus-visible:ring-tele-primary/40"
                                                    value={minutePriceClient}
                                                    onChange={(e) => setMinutePriceClient(e.target.value)}
                                                />
                                                <Input
                                                    placeholder="4.76"
                                                    className="h-9 rounded-xl border-slate-200 bg-white text-sm focus-visible:ring-tele-primary/40"
                                                    value={minutePriceClientShow}
                                                    onChange={(e) => setMinutePriceClientShow(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div className="mt-1 flex items-center justify-between border-t border-slate-200 pt-2 text-[11px]">
                                            <span className="text-slate-500">Platform payout (auto)</span>
                                            <span className="rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-white">
                                                {parseFloat(customerMinutePrice).toFixed(2)} {client?.currency} / min
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid gap-3 border-t border-dashed border-slate-200 pt-3">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                                        Conference pricing
                                    </p>
                                    <PriceRow
                                        label="Conference price – customer"
                                        helper="One-time fee per conference"
                                        currency={client?.currency}
                                        placeholder="300"
                                        value={minutePriceConference}
                                        onChange={(e) => setMinutePriceConference(e.target.value)}
                                    />
                                    <div className="grid gap-2 rounded-xl bg-slate-50 px-3 py-3 text-[11px] text-slate-500">
                                        <div className="grid gap-2 md:grid-cols-3">
                                            <div className="flex items-center justify-between md:block">
                                                <span className="font-medium text-slate-700">Agent share %</span>
                                                <Input
                                                    placeholder="70"
                                                    className="mt-1 h-9 rounded-xl border-slate-200 bg-white text-sm focus-visible:ring-tele-primary/40"
                                                    value={conferencePriceAgent}
                                                    onChange={(e) => setConferencePriceAgent(e.target.value)}
                                                />
                                                <Input
                                                    placeholder="70"
                                                    className="mt-1 h-9 rounded-xl border-slate-200 bg-white text-sm focus-visible:ring-tele-primary/40"
                                                    value={conferencePriceAgentShow}
                                                    readOnly
                                                />
                                            </div>
                                            <div className="flex items-center justify-between md:block">
                                                <span className="font-medium text-slate-700">Team leader %</span>
                                                <Input
                                                    placeholder="10"
                                                    className="mt-1 h-9 rounded-xl border-slate-200 bg-white text-sm focus-visible:ring-tele-primary/40"
                                                    value={conferencePriceGroupLeader}
                                                    onChange={(e) => setConferencePriceGroupLeader(e.target.value)}
                                                />
                                                <Input
                                                    placeholder="10"
                                                    className="mt-1 h-9 rounded-xl border-slate-200 bg-white text-sm focus-visible:ring-tele-primary/40"
                                                    value={conferencePriceGroupLeaderShow}
                                                    readOnly
                                                />
                                            </div>
                                            <div className="flex items-center justify-between md:block">
                                                <span className="font-medium text-slate-700">Client %</span>
                                                <Input
                                                    placeholder="20"
                                                    className="mt-1 h-9 rounded-xl border-slate-200 bg-white text-sm focus-visible:ring-tele-primary/40"
                                                    value={conferencePriceClient}
                                                    onChange={(e) => setConferencePriceClient(e.target.value)}
                                                />
                                                <Input
                                                    placeholder="20"
                                                    className="mt-1 h-9 rounded-xl border-slate-200 bg-white text-sm focus-visible:ring-tele-primary/40"
                                                    value={conferencePriceClientShow}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="mt-1 flex items-center justify-between border-t border-slate-200 pt-2">
                                            <span className="text-slate-500">Platform payout (auto)</span>
                                            <span className="rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-white">
                                                {parseFloat(minutePriceConference).toFixed(2)} {client?.currency} / conf.
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AgentDetailModule;
